import {
  BuildingOffice2Icon,
  HomeModernIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import type { PortfolioOverviewSummary } from "@/components/portfolio/all-estate-overview/types";
import { useLanguage } from "@/context/LanguageContext";
import { formatCZK } from "@/utils/MyEstateHelper";
import { SummaryMetricGrid } from "@/components/ui/SummaryGrid";

type PortfolioOverviewHeaderProps = {
  summary: PortfolioOverviewSummary;
  displayedProfit: number;
};

export function PortfolioOverviewHeader({
  summary,
  displayedProfit,
}: PortfolioOverviewHeaderProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      financialSummary: "Souhrn financí",
      description: "Společné výsledky všech nemovitostí v portfoliu.",
      purchaseValue: "Pořizovací hodnota",
      income: "Příjmy",
      expense: "Výdaje",
      result: "Výsledek hospodaření",
      properties: "Nemovitosti",
      rented: "Pronajato",
      vacant: "Neobsazeno",
    },
    en: {
      financialSummary: "Financial summary",
      description: "Combined results of every property in the portfolio.",
      purchaseValue: "Acquisition value",
      income: "Income",
      expense: "Expenses",
      result: "Operating result",
      properties: "Properties",
      rented: "Rented",
      vacant: "Vacant",
    },
  }[language];

  const metrics = [
    { label: copy.purchaseValue, value: formatCZK(summary.totalPurchasePrice) },
    { label: copy.income, value: formatCZK(summary.totalIncome) },
    { label: copy.expense, value: formatCZK(summary.totalExpense) },
    { label: copy.result, value: formatCZK(displayedProfit) },
  ];
  const structure = [
    {
      label: copy.properties,
      value: summary.estateCount,
      Icon: BuildingOffice2Icon,
    },
    { label: copy.rented, value: summary.occupiedCount, Icon: HomeModernIcon },
    { label: copy.vacant, value: summary.availableCount, Icon: MapPinIcon },
  ];

  return (
    <>
      <div className="flex flex-col gap-4 border-t border-softiq-primary/10 px-5 py-5 dark:border-white/10 sm:flex-row sm:items-center sm:px-6">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-softiq-secondary-shade-200 text-softiq-primary dark:bg-white/10 dark:text-softiq-secondary">
          <BuildingOffice2Icon aria-hidden="true" className="size-6" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="app-panel-title">{copy.financialSummary}</p>
          <p className="app-section-description mt-1">{copy.description}</p>
        </div>
        <div className="flex flex-wrap gap-1.5 sm:justify-end">
          {structure.map(({ label, value, Icon }) => (
            <span key={label} className="app-pill-neutral gap-1.5">
              <Icon aria-hidden="true" className="size-3" />
              {label}: {value}
            </span>
          ))}
        </div>
      </div>

      <SummaryMetricGrid
        layout="four"
        metrics={metrics.map((metric) => ({
          ...metric,
          tone:
            metric.label === copy.result && displayedProfit < 0
              ? ("negative" as const)
              : ("default" as const),
        }))}
      />
    </>
  );
}
